import { useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Upload, X, Image as ImageIcon, Loader2, Link as LinkIcon } from "lucide-react";

/**
 * Resolve the public URL for an image_url field which can be either
 * an external URL (https://...) or a relative API path (/api/files/{id}).
 */
export function resolveImageUrl(url) {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
}

export default function ImageUploader({ value, onChange, label = "Bild", testid = "image-uploader" }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [showUrl, setShowUrl] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setErr("Bitte eine Bilddatei auswählen.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setErr("Datei zu gross (max. 10 MB).");
            return;
        }
        setErr("");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url); // /api/files/{id}
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const onPick = (e) => handleFile(e.target.files?.[0]);
    const onDrop = (e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); };
    const onDragOver = (e) => e.preventDefault();

    const previewSrc = resolveImageUrl(value);

    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary">{label}</label>
                <button type="button" onClick={() => setShowUrl((v) => !v)} className="text-[10px] uppercase tracking-[0.14em] text-ink-tertiary hover:text-navy flex items-center gap-1 transition-colors" data-testid={`${testid}-toggle-url`}>
                    <LinkIcon className="w-3 h-3" />
                    {showUrl ? "Upload statt URL" : "URL eingeben"}
                </button>
            </div>

            {showUrl ? (
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://… oder /api/files/…"
                    data-testid={`${testid}-url-input`}
                    className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
                />
            ) : previewSrc ? (
                <div className="relative group rounded-md overflow-hidden border border-sand-300 bg-sand-deep">
                    <img src={previewSrc} alt="" className="w-full h-40 object-cover" />
                    <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <button type="button" onClick={() => inputRef.current?.click()} className="bg-white text-navy text-xs font-medium px-3 py-1.5 rounded flex items-center gap-1.5 hover:bg-sand transition-colors" data-testid={`${testid}-replace`}>
                            <Upload className="w-3 h-3" /> Ersetzen
                        </button>
                        <button type="button" onClick={() => onChange("")} className="bg-terracotta text-white text-xs font-medium px-3 py-1.5 rounded flex items-center gap-1.5 hover:bg-terracotta-hover transition-colors" data-testid={`${testid}-remove`}>
                            <X className="w-3 h-3" /> Entfernen
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    data-testid={`${testid}-dropzone`}
                    className="w-full h-32 rounded-md border-2 border-dashed border-sand-300 hover:border-navy bg-sand-100 hover:bg-sand-deep flex flex-col items-center justify-center gap-2 text-ink-secondary hover:text-navy transition-colors"
                >
                    {busy ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        <>
                            <ImageIcon className="w-5 h-5" strokeWidth={1.5} />
                            <span className="text-xs font-medium">Bild hochladen oder ziehen</span>
                            <span className="text-[10px] text-ink-tertiary">JPG / PNG / WEBP · max 10 MB</span>
                        </>
                    )}
                </button>
            )}

            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`${testid}-input`} />
            {err && <p className="text-xs text-terracotta mt-2">{err}</p>}
        </div>
    );
}
